import { Delete } from "@mui/icons-material";
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { Autocomplete, Box, Collapse, Container, Icon, IconButton, Table, TableBody, TableCell, TableRow, TextField, Typography } from '@mui/material';
import Grid2 from '@mui/material/Unstable_Grid2';
import CardTotalizador, { CardTotalizadorLabel, CardTotalizadorValue } from "components/cards/CardTotalizador";
import DialogContext from 'components/confirm-dialog-context';
import FloatingActions from "components/floatingactions/FloatingActions";
import InfiniteScrollVirtuoso, { HeaderInfiniteScrollVirtuoso, RowInfiniteScrollVirtuosoProps } from 'components/infinitescrollvirtuoso/InfiniteScrollVirtuoso';
import SkuInput, { SkuInputComponent } from "components/inputs/skuinput/SkuInput";
import { CustomButton } from "components/roundedbutton/CustomButton";
import useCatchApiError, { Problem } from 'hooks/useCatchApiError';
import useHotkey from 'hooks/useHotkey';
import { useSnackbarCustom } from 'hooks/useSnackCustom';
import { ConferenciaRowSituacaoLegenda, ConferenciaRowSituacaoLegendaCor, ConferenciaRowSituacaoLegendaLabel, NAO_CONFERIDOS_KEY_NAME } from 'pages/conferencia/Conferencia';
import { FlowEventoNotaEmitidaTypeDTO } from "pages/evento-nota-fiscal/evento-nota-fiscal.model";
import EventoNotaFiscal, { StateEventoNotaDialog } from "pages/evento-nota-fiscal/EventoNotaFiscal";
import { EventoNotaFiscalEmitida } from "pages/pagamento/pagamento.model";
import CustomLoading from "pages/Venda/Header/CustomLoading";
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { resetConsignado } from "redux/features/consignadoSlice";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { ColumnData, createReadOnly, deepClone, equalsObj, resolveWrapper, sortArray } from 'utils/util';
import ConsignadoService from '../consignado.service';
import { FlowConsignadoDevolucaoListItensItemDTO, FlowConsignadoDevolucaoListItensNotaDTO, FlowConsignadoDevolucaoListItensResponseDTO, Itens } from './consignado-devolucao.model';
import { customColors, theme } from "theme";

type LocalState = {
    skusConferidos: Itens;
    skusNaoConferidos: FlowConsignadoDevolucaoListItensItemDTO[];
    tableData: [string, FlowConsignadoDevolucaoListItensItemDTO[]][];
    seriesExpandidas: string[];
    qtdConferidaTotal: number;
    valorConferidoTotal: number;
    saldoTotal: number;
    valorSaldoTotal: number;
    loading: boolean;
    receivedData: {
        uuid: string;
        cliente: string;
        vendedor?: string;
        origem: 'detail' | 'list';
    } | null;
};

const initialState: LocalState = {
    skusConferidos: {},
    skusNaoConferidos: [],
    tableData: [],
    seriesExpandidas: [],
    qtdConferidaTotal: 0,
    valorConferidoTotal: 0,
    saldoTotal: 0,
    valorSaldoTotal: 0,
    loading: true,
    receivedData: null
};

const ConsignadoDevolucao = () => {

    const [localState, setLocalState] = useState(deepClone(initialState));
    const navigate = useNavigate();
    const { addErrorWithSound, addError } = useSnackbarCustom();
    const dialog = useContext(DialogContext);
    const consignadoService = ConsignadoService();
    const location = useLocation();
    const receivedParams: { uuid: string; cliente: string; vendedor?: string; origem: 'list' | 'detail'; } = location.state;
    const { throwErrorMessage } = useCatchApiError();
    const dispatch = useAppDispatch();
    const estabelecimentoSelecionado = useAppSelector(s => s.estabelecimentoSelecionado);

    const initialStateEventoNotaDialog: StateEventoNotaDialog = {
        open: false,
        info: {
            eventoType: FlowEventoNotaEmitidaTypeDTO.SEM_EVENTO,
            header: 'Devolução concluída',
        }
    };

    const [stateEventoNotaDialog, setStateEventoNotaDialog] = useState(initialStateEventoNotaDialog);

    useHotkey([
        {
            keys: ['l', 'L'],
            alt: true,
            action: () => {
                limparConferencia();
            },
        },
    ]);

    useEffect(() => {
        loadItens();
        return () => {
            handleResetAll(false);
            dispatch(resetConsignado());
        };
        //eslint-disable-next-line
    }, []);

    const skuinputRef = useRef<SkuInputComponent>(null);
    const conferencia = useRef<FlowConsignadoDevolucaoListItensResponseDTO>();
    const getQuantidadeTotal = () => conferencia.current?.itens.reduce((acc, curr) => acc + curr.quantidade, 0) ?? 0;

    const columns: ColumnData<FlowConsignadoDevolucaoListItensItemDTO & { __saldo: number; __options: unknown; }>[] = [
        {
            header: "SKU's",
            id: 'sku',
            key: 'skuDescritivo',
            type: 'string',
            width: 36,
        },

        {
            header: 'Nota',
            id: 'nota',
            key: 'notaDescritivo',
            type: 'string',
            width: 15,
            textAlign: 'center',
        },


        {
            header: 'Quantidade da nota',
            id: 'quantidade',
            key: 'quantidade',
            type: 'number',
            width: 15,
            textAlign: 'center',
        },

        {
            header: 'Quantidade conferida',
            id: 'conferido',
            key: 'conferido',
            type: 'number',
            width: 15,
            textAlign: 'center',
        },

        {
            header: 'Saldo',
            id: 'saldo',
            key: '__saldo',
            type: 'number',
            width: 15,
            textAlign: 'center',
            convert: (item, { extraData }) => {

                if (extraData.naoConferido) {
                    return <>{item.quantidade}</>;
                }

                return <>
                    {item.saldo}
                </>;
            }
        },

        {
            header: '',
            id: 'options',
            key: '__options',
            type: 'custom',
            width: 4,
            textAlign: 'center',
            convert: item => {
                return <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        width: `100%`
                    }}
                >
                    <IconButton
                        onClick={() => handleDeleteRow(item)}
                        sx={{
                            color: theme.palette.primary.main,
                            marginLeft: '5px',
                        }}
                    >
                        <Delete />
                    </IconButton >
                </Box>;
            }
        },
    ];

    const estabRef = useRef(estabelecimentoSelecionado.uuid);

    useEffect(() => {
        if (estabRef.current !== estabelecimentoSelecionado.uuid) {
            navigate('/consignado');
        }
        //eslint-disable-next-line
    }, [estabelecimentoSelecionado]);

    const handleDeleteRow = (item: FlowConsignadoDevolucaoListItensItemDTO) => {
        const { serie, skuUUID, skuDescritivo, conferido, chaveAcesso } = item;
        dialog.confirm({
            title: 'Atenção',
            message: `Deseja excluir ${skuDescritivo}?`,
            onConfirm: () => {
                setLocalState(prev => {
                    const notas = conferencia.current?.notas[skuUUID] ?? [];
                    const nota = notas.find(s => s.chaveAcesso === chaveAcesso);
                    if (!nota) {
                        addErrorWithSound({ message: 'SKU não faz parte do pedido' });
                        return prev;
                    }
                    nota.saldo += conferido;

                    const skusConferidos = prev.skusConferidos;
                    const filter = skusConferidos[serie].filter(s => s.skuUUID !== skuUUID || s.chaveAcesso !== chaveAcesso);
                    if (!filter.length) {
                        delete skusConferidos[serie];
                    } else {
                        skusConferidos[serie] = filter;
                    }
                    atualizarSaldoSkus(skusConferidos, conferencia.current?.itens.find(s => s.skuUUID === skuUUID)?.quantidade ?? 0, skuUUID);
                    const skusNaoConferidos = atualizarSaldoSkusNaoConferidos(prev.skusNaoConferidos, conferido, skuUUID);

                    return {
                        ...prev,
                        skusConferidos,
                        skusNaoConferidos
                    };
                });
            }
        });
    };

    const cards = [
        {
            label: 'Consignado',
            getQtd: () => getQuantidadeTotal(),
        },
        {
            label: 'Conferido',
            getQtd: () => localState.qtdConferidaTotal,
        },
        {
            label: 'Saldo à conferir',
            getQtd: () => localState.saldoTotal,
        },
    ];

    const atualizarSaldoSkus = (produtos: Itens, qtdPedida: number, skuUUID: string) => {
        const skus = Object.values(produtos).flat().filter(s => s.skuUUID === skuUUID);
        const conferido = skus.reduce((acc, curr) => acc + curr.conferido, 0);
        const saldoGeral = qtdPedida - conferido;
        skus.forEach(s => s.saldo = saldoGeral);
    };

    const atualizarSaldoSkusNaoConferidos = (skusNaoConferidos: FlowConsignadoDevolucaoListItensItemDTO[], qtdConferida: number, skuUUID: string) => {
        let item = skusNaoConferidos.find(s => s.skuUUID === skuUUID);
        if (!item) {
            item = conferencia.current!.itens.filter(s => s.skuUUID === skuUUID)[0];
            item.conferido = item.quantidade - qtdConferida;
            skusNaoConferidos = sortArray([...skusNaoConferidos, item], { fieldToSort: 'skuDescritivo' });
        } else {
            item.conferido -= qtdConferida;
        }

        return skusNaoConferidos;
    };

    const getUltimaNotaSaldoFaltante = (notas: FlowConsignadoDevolucaoListItensNotaDTO[]) => {
        for (let i = notas.length - 1; i >= 0; i--) {
            const n = notas[i];
            if (n.saldo < n.quantidade) {
                return n;
            }
        }
    };

    const handleSelectSku = (value: FlowConsignadoDevolucaoListItensItemDTO | null, isAdding: boolean) => {
        if (!value) {
            return addErrorWithSound({ message: 'SKU não faz parte do pedido' });
        }

        const { skuUUID } = value;

        const notas = conferencia.current?.notas[skuUUID] ?? [];
        if (isAdding) {
            const primeiraNotaComSaldo = notas.find((n) => n.saldo > 0);

            if (!primeiraNotaComSaldo) {
                return addErrorWithSound({ message: 'Sem saldo disponível para este SKU nas notas.' });
            }
            const serie = primeiraNotaComSaldo.serie;
            const existingItem = localState.skusConferidos[serie]?.find((item) => item.skuUUID === skuUUID && item.chaveAcesso === primeiraNotaComSaldo.chaveAcesso);

            let toSet: FlowConsignadoDevolucaoListItensItemDTO;
            if (!existingItem) {
                toSet = {
                    ...value,
                    serie: primeiraNotaComSaldo.serie,
                    notaDescritivo: primeiraNotaComSaldo.notaDescritivo,
                    chaveAcesso: primeiraNotaComSaldo.chaveAcesso,
                    quantidade: primeiraNotaComSaldo.quantidade,
                    saldo: value.quantidade,
                    conferido: 1,
                };
            } else {
                toSet = {
                    ...existingItem,
                    conferido: existingItem.conferido + 1,
                };
            }

            setLocalState(prev => {
                const skusConferidos = prev.skusConferidos;
                const skusToSet = (skusConferidos[serie] ?? []).filter(s => !(s.skuUUID === toSet.skuUUID && s.chaveAcesso === toSet.chaveAcesso));
                skusConferidos[serie] = sortArray([...skusToSet, toSet], { fieldToSort: ['skuDescritivo', 'notaDescritivo'] });
                atualizarSaldoSkus(skusConferidos, value.quantidade, toSet.skuUUID);
                const skusNaoConferidos = atualizarSaldoSkusNaoConferidos(prev.skusNaoConferidos, -1, skuUUID);

                return {
                    ...prev,
                    skusConferidos,
                    skusNaoConferidos,
                    seriesExpandidas: [...prev.seriesExpandidas.filter(s => s !== serie), serie],
                };
            });
            primeiraNotaComSaldo.saldo--;
        } else {
            const ultimaNotaSaldoFaltante = getUltimaNotaSaldoFaltante(notas);

            if (!ultimaNotaSaldoFaltante) {
                return addErrorWithSound({ message: 'Todos os itens desse skus já foram removidos.' });
            }

            const serie = ultimaNotaSaldoFaltante.serie;
            setLocalState(prev => {
                const skusConferidos = prev.skusConferidos;
                const sku = skusConferidos[serie].find(s => s.skuUUID === skuUUID);
                if (!sku) {
                    addErrorWithSound({ message: 'SKU não encontrado!' });
                    return prev;
                }
                if (sku.conferido === 1) {
                    const filter = skusConferidos[serie].filter(s => s.skuUUID !== skuUUID);
                    if (filter.length) {
                        skusConferidos[serie] = filter;
                    } else {
                        delete skusConferidos[serie];
                    }
                } else {
                    sku.conferido--;
                }

                ultimaNotaSaldoFaltante.saldo++;
                atualizarSaldoSkus(skusConferidos, value.quantidade, skuUUID);
                const skusNaoConferidos = atualizarSaldoSkusNaoConferidos(prev.skusNaoConferidos, 1, skuUUID);

                return {
                    ...prev,
                    skusConferidos,
                    skusNaoConferidos,
                    seriesExpandidas: [...prev.seriesExpandidas.filter(s => s !== serie), serie],
                };
            });
        }
    };

    const getOptions = () => {
        return sortArray(conferencia.current?.itens ?? [], { fieldToSort: 'skuDescritivo' });
    };

    const limparConferencia = () => {
        dialog.confirm({
            error: true,
            title: 'Atenção',
            message: `Todos os dados da conferência serão apagados. Deseja realmente continuar?`,
            onConfirm: () => {
                handleResetAll();
            }
        });
    };

    const handleResetAll = (shouldLoadItens = true) => {
        setLocalState(deepClone(initialState));
        setStateEventoNotaDialog(deepClone(initialStateEventoNotaDialog));
        resetFocus();
        if (shouldLoadItens) {
            loadItens();
        }
    };

    const resetFocus = useCallback(() => {
        skuinputRef.current?.focus();
    }, []);


    const atualizaTableData = () => {
        const tableData = sortArray(Object.entries(localState.skusConferidos), { fieldToSort: '0' });
        if (!!localState.skusNaoConferidos.length) {
            tableData.push([NAO_CONFERIDOS_KEY_NAME, localState.skusNaoConferidos]);
        }
        setLocalState(prev => ({ ...prev, tableData }));
    };

    useEffect(atualizaTableData,
        //eslint-disable-next-line
        [JSON.stringify(localState.skusConferidos), JSON.stringify(localState.skusNaoConferidos)]);

    const atualizaSkusNaoConferidos = () => {
        setLocalState(prev => {
            const qtdConferidaTotal = Object.values(prev.skusConferidos).flat().reduce((acc, curr) => acc + curr.conferido, 0);
            const qtdTotal = getQuantidadeTotal();
            return {
                ...prev,
                qtdConferidaTotal,
                saldoTotal: qtdTotal - qtdConferidaTotal,
                skusNaoConferidos: prev.skusNaoConferidos.filter(s => s.conferido !== s.quantidade)
            };
        });
    };

    useEffect(atualizaSkusNaoConferidos,
        //eslint-disable-next-line
        [JSON.stringify(localState.skusConferidos)]
    );

    const loadItens = () => {
        consignadoService.getItensDevolucao(receivedParams.uuid!)
            .then(({ data }) => {
                conferencia.current = createReadOnly(data);
                setLocalState(prev => ({
                    ...prev,
                    skusNaoConferidos: data.itens,
                    saldoTotal: getQuantidadeTotal(),
                    receivedData: prev.receivedData ?? receivedParams,
                }));
            })
            .catch(throwErrorMessage)
            .finally(() => {
                setLocalState(prev => ({ ...prev, loading: false }));
            });
    };

    const devolver = (isSimbolica: boolean) => {

        if (equalsObj(localState.skusConferidos, {})) {
            return addError({ message: 'É necessário ter pelo menos uma peça conferida para realizar a devolução!', closeable: true, persist: true, id: 'sem-conferidos', preventDuplicate: true });
        }

        const onConcluirFluxo = (data: EventoNotaFiscalEmitida) => {
            if (!data) {
                return handleNavigateBack();
            }
            setLocalState(prev => ({ ...prev, loading: false }));
            setStateEventoNotaDialog(prev => {
                return {
                    open: true,
                    info: {
                        ...prev.info,
                        eventoEntrada: data,
                        eventoType: data.situacaoSefaz === 'AUTORIZADA'
                            ? FlowEventoNotaEmitidaTypeDTO.SAIDA_AUTORIZADA
                            : data.situacaoSefaz === 'EM PROCESSAMENTO'
                                ? FlowEventoNotaEmitidaTypeDTO.SAIDA_PROCESSAMENTO
                                : FlowEventoNotaEmitidaTypeDTO.SAIDA_REJEITADA
                    }
                };
            });

        };
        setLocalState(prev => ({ ...prev, loading: true }));

        consignadoService.confirmarDevolver(localState.receivedData!.uuid, localState.skusConferidos, isSimbolica)
            .then(({ data }) => {
                onConcluirFluxo(data);
            })
            .catch(err => {
                setLocalState(prev => ({ ...prev, loading: false }));
                const problema: Problem = err.response.data;
                if (problema.code === 'SERIE_NAO_PRESENTE_NA_CONFIGURACAO') {
                    dialog.confirm({
                        error: false,
                        title: 'Deseja confirmar as devoluções?',
                        message: problema.detail,
                        onConfirm: () => {
                            consignadoService.devolver(localState.receivedData!.uuid, localState.skusConferidos, isSimbolica)
                                .then(({ data }) => {
                                    onConcluirFluxo(data);
                                })
                                .catch(err => {
                                    throwErrorMessage(err);
                                });
                        },
                        onCancel: () => { }
                    });
                } else if (problema.code === 'BUSINESS_EXCEPTION' && problema.detail === 'validateSituacoesNotasSaida') {
                    dialog.confirm({
                        error: true,
                        title: 'Não é possível realizar a devolução',
                        message: <ValidateSituacaoNotasSaidasDevolucao problema={problema} />,
                        onConfirm: () => { },
                    });
                } else {
                    throwErrorMessage(err);
                }
            });
    };

    const handleNavigateBack = () => {
        if (receivedParams.origem === 'detail') {
            navigate('/registra-consignado', { state: { uuid: receivedParams.uuid! } });
        } else {
            navigate('/consignado');
        }
    };

    const TableCustomRow = ({ calcWidth, columns, data, index }: RowInfiniteScrollVirtuosoProps<[string, FlowConsignadoDevolucaoListItensItemDTO[]], any>) => {
        const [key, itens] = data;
        const isNaoConferidos = () => {
            return key === NAO_CONFERIDOS_KEY_NAME;
        };

        const isOpen = () => {
            return isNaoConferidos() ? true : localState.seriesExpandidas.includes(key);
        };

        const toggleOpen = () => {
            setLocalState(prev => {
                let expandedRows = prev.seriesExpandidas;
                const prevOpen = expandedRows.includes(key);
                if (!prevOpen) expandedRows = [...expandedRows, key];
                else expandedRows = expandedRows.filter(c => c !== key);
                return { ...prev, seriesExpandidas: expandedRows };
            });
            resetFocus();
        };

        const getColorRow = (item: FlowConsignadoDevolucaoListItensItemDTO) => {
            if (isNaoConferidos()) return '';
            if (item.saldo === 0) return customColors.confereridoBg;
            return customColors.confereridoParcialBg;
        };

        const shuldShowColumn = (index: number) => {
            if (!isNaoConferidos()) return true;
            const isSkuColumn = index === 0;
            const isQuantidadeColumn = index === 0;
            const isSaldoColumn = index === 4;
            return isSkuColumn || isSaldoColumn || isQuantidadeColumn;
        };

        const getContentCell = (item: FlowConsignadoDevolucaoListItensItemDTO, col: ColumnData<any>, index: number) => {
            if (!shuldShowColumn(index)) return;
            return col.convert?.(item as any, { column: col, extraData: { calcWidth, naoConferido: isNaoConferidos() } }) ?? resolveWrapper(item, col.key);
        };

        return <>
            <TableCell
                colSpan={999}
                style={{ padding: 0 }}
            >
                <Table>
                    <TableBody>
                        <TableRow>
                            <TableCell colSpan={999} sx={{ padding: 0, lineHeight: '30px' }}>
                                <Box display={'flex'} alignItems={'center'} sx={{
                                    height: '40px',
                                }}>
                                    {!isNaoConferidos() &&
                                        <IconButton
                                            aria-label="expand row"
                                            size="small"
                                            onClick={toggleOpen}
                                            sx={{
                                                paddingLeft: '15px'
                                            }}
                                        >
                                            {isOpen() ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                                        </IconButton>
                                    }

                                    <Typography
                                        fontWeight={600}
                                        textAlign={'left'}
                                        sx={{
                                            paddingLeft: '15px',
                                            width: isNaoConferidos() ? calcWidth((Number(columns[0].width) + Number(columns[1].width)) + Number(columns[2].width) + Number(columns[3].width), 15) : '100%',
                                        }}
                                    >
                                        {isNaoConferidos() ? '' : 'Série: '}{key}
                                    </Typography>

                                    {isNaoConferidos() &&
                                        <>
                                            <Typography
                                                fontWeight={600}
                                                sx={{
                                                    width: calcWidth((Number(columns[3].width), 14)),
                                                    textAlign: 'center',
                                                    paddingLeft: '15px',
                                                }}
                                            >
                                                Quantidade total
                                            </Typography>
                                        </>
                                    }
                                </Box>
                            </TableCell>
                        </TableRow>

                        <TableRow sx={{ '& > *': { borderBottom: 'unset' } }}>
                            <TableCell style={{ padding: 0 }} colSpan={999}>
                                <Collapse in={isOpen()} timeout="auto" unmountOnExit>
                                    <Table size="small" aria-label="purchases">
                                        <TableBody>
                                            {itens.map((item, indexRow) => {
                                                return <TableRow
                                                    key={item.serie + "-" + item.skuUUID + "-" + indexRow + "-row"}
                                                    sx={{
                                                        background: getColorRow(item),
                                                    }}
                                                >
                                                    {columns.map((col, indexCol) => {
                                                        return <TableCell
                                                            key={col.key + index + indexRow + indexCol + '-row'}
                                                            sx={{
                                                                height: '30px',
                                                                minWidth: col.minWidth,
                                                                maxWidth: col.maxWidth,
                                                                width: calcWidth(col.width, 15),
                                                                textAlign: col.textAlign,
                                                                fontSize: '14px',
                                                                padding: 0,
                                                                paddingLeft: '15px',
                                                                color: theme.palette.common.black,
                                                                borderBottom: indexRow === itens.length - 1 ? 'unset' : undefined,
                                                            }}
                                                        >
                                                            {
                                                                getContentCell(item, col, indexCol)
                                                            }
                                                        </TableCell>;
                                                    })}
                                                </TableRow>;
                                            })}

                                        </TableBody>
                                    </Table>
                                </Collapse>
                            </TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </TableCell>
        </>;
    };

    return (
        <Container
            maxWidth="xl"
            sx={{
                margin: '20px auto',
                display: 'grid',
                gap: '20px',
            }}>
            <Grid2 container spacing={2}>

                <Grid2 xs={12} sm={6} lg={6} >
                    <Autocomplete
                        disabled={true}
                        value={localState.receivedData?.cliente ?? 'CONSUMIDOR'}
                        id="cliente"
                        options={[]}
                        forcePopupIcon={false}
                        renderInput={
                            (params) =>
                                <TextField {...params}
                                    label="Cliente"
                                    variant="standard"
                                    InputLabelProps={{
                                        sx: {
                                            marginLeft: '5px'
                                        }
                                    }}
                                />}
                    />
                </Grid2>

                <Grid2 xs={12} sm={6} lg={6} >
                    <Autocomplete
                        disabled={true}
                        value={localState.receivedData?.vendedor ?? 'SEM VENDEDOR'}
                        id="vendedor"
                        options={[]}
                        forcePopupIcon={false}
                        renderInput={
                            (params) =>
                                <TextField {...params}
                                    label="Vendedor Principal"
                                    variant="standard"
                                    InputLabelProps={{
                                        sx: {
                                            marginLeft: '5px'
                                        }
                                    }}
                                />}
                    />
                </Grid2>

                <Grid2 xs={12} sm={6} lg={6}>
                    <SkuInput
                        ref={skuinputRef}
                        customOptions={getOptions()}
                        onSelect={handleSelectSku}
                        getKey={s => s.skuUUID}
                        getOptionLabel={s => s.skuDescritivo}
                    />
                </Grid2>

            </Grid2>

            <Grid2 container spacing={2}>
                <Grid2 xs={12} height="auto">
                    <InfiniteScrollVirtuoso
                        data={localState.tableData}
                        heightScale={'430px'}
                        renderItem={(tableProps) => TableCustomRow({ ...tableProps, columns })}
                        fixedHeaderContent={(tableProps) => HeaderInfiniteScrollVirtuoso({ ...tableProps, columns })}
                    />
                </Grid2>
            </Grid2>

            <Grid2 container spacing={2} sx={{ textAlign: "center" }}>

                {cards.map((c, i) => {
                    return <Grid2 key={i} xs={12} sm={6} md={4} lg={4}>
                        <CardTotalizador
                            id="abc"
                        >
                            <CardTotalizadorValue
                                id={c.label + '-qtd'}
                                value={c.getQtd()}
                                color={theme.palette.primary.main}
                            />

                            <CardTotalizadorLabel
                                id={c.label}
                                label={c.label}
                            />
                        </CardTotalizador>
                    </Grid2>;
                })}
            </Grid2>

            <Grid2 container spacing={2} sx={{ textAlign: "center", display: 'flex', justifyContent: 'space-between' }}>
                <Box sx={{
                    display: 'flex',
                    maxHeight: '50px',
                    position: 'relative',
                    gap: '10px',
                    alignItems: 'flex-end',
                }}>
                    <CustomButton
                        variant="contained"
                        startIcon={<Icon className='fa-solid fa-arrow-left' sx={{ width: '25px' }} />}
                        containerStyle={{
                            boxShadow: "2px 2px 10px rgba(0,0,0,0.35)"
                        }}
                        color="error"
                        onClick={() => {
                            handleNavigateBack();
                        }}
                    >
                        Voltar
                    </CustomButton>

                    {Object.values(ConferenciaRowSituacaoLegenda).map(key => {
                        return <Box key={`legenda-${key}`}>
                            <Box sx={{
                                height: '20px',
                                width: '105px',
                                bgcolor: ConferenciaRowSituacaoLegendaCor[key],
                                border: '1px solid grey',
                                borderRadius: '5px'
                            }} >
                            </Box>
                            <Typography sx={{ fontSize: '12px', textAlign: 'center' }}>
                                {ConferenciaRowSituacaoLegendaLabel[key]}
                            </Typography>
                        </Box>;
                    })}
                </Box>

                <Box sx={{
                    display: 'flex',
                    maxHeight: '50px',
                    position: 'relative',
                    gap: '10px',

                    alignItems: 'flex-end',
                }}>
                    <CustomButton
                        variant="contained"
                        startIcon={<Icon className='fa-solid fa-check' sx={{ width: '25px' }} />}
                        containerStyle={{
                            boxShadow: "2px 2px 10px rgba(0,0,0,0.35)"
                        }}
                        color='success'
                        onClick={() => devolver(false)}
                    >
                        Devolver
                    </CustomButton>

                    <FloatingActions
                        actions={[
                            {
                                icon: 'fa-solid fa-eraser',
                                label: 'Limpar conferência (Alt + L)',
                                onClick: limparConferencia,
                                id: 'limpar-conferencia'
                            },
                            // {
                            //     icon: 'fa-solid fa-eraser',
                            //     label: 'Devolver simbólico',
                            //     onClick: () => devolver(true),
                            //     id: 'devolver-simbolico'
                            // },
                        ]}
                        onClose={(r) => {
                            if (['blur', 'toggle'].includes(r)) {
                                resetFocus();
                            }
                        }}
                    />
                </Box>
            </Grid2>

            {localState.loading && <CustomLoading />}

            <EventoNotaFiscal
                state={stateEventoNotaDialog}
                setState={setStateEventoNotaDialog}
                onClose={() => {
                    handleNavigateBack();
                    setTimeout(() => {
                        handleResetAll(false);
                    },);
                }}
            />

        </Container>
    );
};

export default ConsignadoDevolucao;

export const ValidateSituacaoNotasSaidasDevolucao = ({ problema }: { problema: Problem; }) => {
    return <Box
        sx={{
            display: 'flex',
            flexDirection: 'column',
        }}
    >
        <Box sx={{ marginBottom: '15px' }}>Foram encontradas notas de saída não autorizadas ou pendentes relacionadas a esta operação:</Box>
        {problema.objects.map((s, i) => {
            return <Box key={i}>
                {s.userMessage}
            </Box>;
        })}
        <Box sx={{ marginTop: '15px' }}>Regularize a(s) nota(s) antes de prosseguir.</Box>
    </Box>;
};
